import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { ImagePickButton } from '../components/ImageGallery.js';
import { ErrorState, StatusBadge } from '../components/ui.js';

/**
 * Hromadná údržba šablon: odstranění odpočtu pod logem a doplnění obrázku do bannerů bez obrázku.
 * Vždy se nejdřív ukáže, kterých šablon se to týká; změny se ukládají jako koncepty a nic se nepublikuje samo.
 */
export function TemplateMaintenance({ canWrite }: { canWrite: boolean }): JSX.Element | null {
  const [countdown, setCountdown] = useState<string[] | null>(null);
  const [countdownDone, setCountdownDone] = useState<number | null>(null);
  const [image, setImage] = useState('');
  const [banners, setBanners] = useState<Array<{ key: string; banners: number }> | null>(null);
  const [bannersDone, setBannersDone] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const strip = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<{ templates: string[] }>('/api/templates/strip-block', {
        type: 'countdown',
        dryRun,
      }),
    onSuccess: (r, dryRun) => {
      setError(null);
      if (dryRun) setCountdown(r.templates);
      else setCountdownDone(r.templates.length);
    },
    onError: (e: Error) => setError(e.message),
  });
  const fill = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<{ templates: Array<{ key: string; banners: number }> }>(
        '/api/templates/banner-images',
        { image, alt: '', dryRun },
      ),
    onSuccess: (r, dryRun) => {
      setError(null);
      if (dryRun) setBanners(r.templates);
      else setBannersDone(r.templates.length);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!canWrite) return null;
  return (
    <details data-testid="template-maintenance">
      <summary>Hromadná údržba šablon (odpočet pod logem, obrázky v bannerech)</summary>
      <div className="stack">
        <p className="note">
          Obě akce nejdřív ukážou, kterých šablon se týkají, a pak uloží nové <b>koncepty</b>.
          Publikované verze se nemění, dokud koncepty sami nepublikujete.
        </p>

        <h4 className="mini">1. Odstranit odpočet („Do závodu zbývá …“) ze všech šablon</h4>
        <div className="row">
          <button type="button" className="btn" onClick={() => strip.mutate(true)}>
            Zjistit, kterých šablon se týká
          </button>
          {countdown && countdown.length > 0 ? (
            <button type="button" className="btn pri" onClick={() => strip.mutate(false)}>
              Odstranit z {countdown.length} šablon
            </button>
          ) : null}
        </div>
        {countdown ? (
          <p className="note">
            {countdown.length === 0 ? 'Žádná šablona odpočet neobsahuje.' : countdown.join(', ')}
          </p>
        ) : null}
        {countdownDone !== null ? (
          <StatusBadge tone="ok" icon="check">
            hotovo, uloženo {countdownDone} konceptů; publikujte je v editoru
          </StatusBadge>
        ) : null}

        <h4 className="mini">2. Doplnit obrázek do bannerů, které ho nemají</h4>
        <div className="row">
          <input
            value={image}
            placeholder="adresa obrázku (https://…)"
            aria-label="Adresa obrázku"
            onChange={(e) => setImage(e.target.value)}
          />
          <ImagePickButton onPick={setImage} />
          <button
            type="button"
            className="btn"
            disabled={!/^https?:\/\//.test(image)}
            onClick={() => fill.mutate(true)}
          >
            Zjistit, kterých šablon se týká
          </button>
          {banners && banners.length > 0 ? (
            <button type="button" className="btn pri" onClick={() => fill.mutate(false)}>
              Doplnit do {banners.length} šablon
            </button>
          ) : null}
        </div>
        {banners ? (
          <p className="note">
            {banners.length === 0
              ? 'Všechny bannery už obrázek mají.'
              : banners.map((b) => `${b.key} (${b.banners}×)`).join(', ')}
          </p>
        ) : null}
        {bannersDone !== null ? (
          <StatusBadge tone="ok" icon="check">
            hotovo, uloženo {bannersDone} konceptů; publikujte je v editoru
          </StatusBadge>
        ) : null}
        {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}
      </div>
    </details>
  );
}
